import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { scrollToSection } from '../utils/animations';
import { getImageUrl } from '../utils/images';

/**
 * Header Component - Modern Variant
 *
 * Sticky navigation header for KimRose Center website with modern glassmorphism design.
 *
 * Features:
 * - Glassmorphism effect on scroll
 * - Smooth Framer Motion animations
 * - Modern hover effects
 * - KimRose logo and branding
 * - Smooth scroll navigation
 * - Responsive design
 * - Throttled scroll handler for performance
 */
export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    scrollToSection(sectionId, 80);
  };

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // If not on home page, navigate there
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'glass-effect shadow-modern'
          : 'bg-white shadow-modern'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <nav className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo and Brand */}
          <motion.div whileHover={{ scale: 1.05 }}>
            <Link
              to="/"
              className="flex items-center space-x-3 focus:outline-none focus:ring-2 focus:ring-modern-sage focus:ring-offset-2 rounded"
            >
              <img
                src={getImageUrl('/images/kimrose_logo_transparent-2.png')}
                alt="KimRose Center Logo"
                className="h-12 w-auto"
              />
              <span className="text-xl font-bold text-modern-forest heading-modern">
                KimRose Center
              </span>
            </Link>
          </motion.div>

          {/* Hamburger Button - Mobile Only */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-modern-sage"
            aria-label="Toggle menu"
          >
            <motion.div
              animate={isMobileMenuOpen ? 'open' : 'closed'}
              className="flex flex-col justify-center items-center"
            >
              <motion.span
                variants={{
                  closed: { rotate: 0, y: 0 },
                  open: { rotate: 45, y: 6 }
                }}
                className="w-6 h-0.5 bg-modern-forest mb-1.5 transition-all"
              />
              <motion.span
                variants={{
                  closed: { opacity: 1 },
                  open: { opacity: 0 }
                }}
                className="w-6 h-0.5 bg-modern-forest mb-1.5 transition-all"
              />
              <motion.span
                variants={{
                  closed: { rotate: 0, y: 0 },
                  open: { rotate: -45, y: -6 }
                }}
                className="w-6 h-0.5 bg-modern-forest transition-all"
              />
            </motion.div>
          </button>

          {/* Desktop Navigation Menu */}
          <ul className="hidden md:flex space-x-6 items-center">
            <li>
              <motion.div whileHover={{ y: -2 }}>
                <a
                  href="/"
                  onClick={handleHomeClick}
                  className="font-medium transition-colors hover:text-modern-sage focus:outline-none focus:ring-2 focus:ring-modern-sage focus:ring-offset-2 rounded px-2 py-1 text-modern-forest relative group"
                >
                  Home
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-modern-sage group-hover:w-full transition-all duration-300" />
                </a>
              </motion.div>
            </li>
            <li>
              <motion.div whileHover={{ y: -2 }}>
                <a
                  href="#about-section"
                  onClick={(e) => handleNavClick(e, 'about-section')}
                  className="font-medium transition-colors hover:text-modern-sage focus:outline-none focus:ring-2 focus:ring-modern-sage focus:ring-offset-2 rounded px-2 py-1 text-modern-forest relative group"
                >
                  About
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-modern-sage group-hover:w-full transition-all duration-300" />
                </a>
              </motion.div>
            </li>
            <li>
              <motion.div whileHover={{ y: -2 }}>
                <a
                  href="#services-section"
                  onClick={(e) => handleNavClick(e, 'services-section')}
                  className="font-medium transition-colors hover:text-modern-sage focus:outline-none focus:ring-2 focus:ring-modern-sage focus:ring-offset-2 rounded px-2 py-1 text-modern-forest relative group"
                >
                  Services
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-modern-sage group-hover:w-full transition-all duration-300" />
                </a>
              </motion.div>
            </li>
            <li>
              <motion.div whileHover={{ y: -2 }}>
                <a
                  href="#contact-section"
                  onClick={(e) => handleNavClick(e, 'contact-section')}
                  className="font-medium transition-colors hover:text-modern-sage focus:outline-none focus:ring-2 focus:ring-modern-sage focus:ring-offset-2 rounded px-2 py-1 text-modern-forest relative group"
                >
                  Contact
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-modern-sage group-hover:w-full transition-all duration-300" />
                </a>
              </motion.div>
            </li>
            <li>
              <motion.a
                href="#contact-section"
                onClick={(e) => handleNavClick(e, 'contact-section')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-modern-sage text-white px-6 py-2 rounded-lg font-semibold shadow-modern hover:shadow-modern-lg transition-all"
              >
                Get Started
              </motion.a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Menu */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: isMobileMenuOpen ? 0 : '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 h-full w-64 bg-white shadow-2xl z-50 md:hidden"
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <span className="text-lg font-bold text-modern-forest">Menu</span>
            <button
              onClick={closeMobileMenu}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
            >
              <svg
                className="w-6 h-6 text-modern-forest"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Mobile Menu Items */}
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-4">
              <li>
                <a
                  href="/"
                  onClick={handleHomeClick}
                  className="block px-4 py-3 rounded-lg font-medium text-modern-forest hover:bg-gray-100 transition-colors"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="#about-section"
                  onClick={(e) => handleNavClick(e, 'about-section')}
                  className="block px-4 py-3 rounded-lg font-medium text-modern-forest hover:bg-gray-100 transition-colors"
                >
                  About
                </a>
              </li>
              <li>
                <a
                  href="#services-section"
                  onClick={(e) => handleNavClick(e, 'services-section')}
                  className="block px-4 py-3 rounded-lg font-medium text-modern-forest hover:bg-gray-100 transition-colors"
                >
                  Services
                </a>
              </li>
              <li>
                <a
                  href="#contact-section"
                  onClick={(e) => handleNavClick(e, 'contact-section')}
                  className="block px-4 py-3 rounded-lg font-medium text-modern-forest hover:bg-gray-100 transition-colors"
                >
                  Contact
                </a>
              </li>
            </ul>
          </nav>

          {/* Mobile Menu Footer - CTA */}
          <div className="p-4 border-t">
            <a
              href="#contact-section"
              onClick={(e) => {
                closeMobileMenu();
                handleNavClick(e, 'contact-section');
              }}
              className="block w-full bg-modern-sage text-white text-center px-6 py-3 rounded-lg font-semibold shadow-modern hover:shadow-modern-lg transition-all"
            >
              Get Started
            </a>
          </div>
        </div>
      </motion.div>
    </motion.header>
  );
}; 